import { useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Section from './Section';
import api from '../../api/axios';

const EMPTY = { name: '', email: '', phone: '', subject: '', message: '', website: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Escribe tu nombre';
    if (!EMAIL_RE.test(form.email)) errors.email = 'Escribe un correo válido';
    if (!form.subject.trim()) errors.subject = 'Escribe un asunto';
    if (form.message.trim().length < 10) errors.message = 'Cuéntame un poco más (mínimo 10 caracteres)';
    return errors;
}

export default function Contact() {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(null); // { type: 'success' | 'error', text }
    const [sending, setSending] = useState(false);

    const onChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        const found = validate(form);
        setErrors(found);
        if (Object.keys(found).length) return;

        setSending(true); setStatus(null);
        try {
            await api.post('/contact/', form);
            setForm(EMPTY);
            setStatus({ type: 'success', text: '¡Gracias! Recibí tu mensaje y te responderé pronto.' });
        } catch (err) {
            const text = err?.response?.status === 429
                ? 'Has enviado varios mensajes seguidos. Intenta de nuevo más tarde.'
                : 'No se pudo enviar el mensaje. Intenta de nuevo en unos minutos.';
            setStatus({ type: 'error', text });
        } finally {
            setSending(false);
        }
    };

    return (
        <Section id="contacto" eyebrow="Trabajemos juntos" title="Contacto" muted>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 3fr' }, gap: { xs: 4, md: 6 } }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>¿Tienes un proyecto en mente?</Typography>
                    <Typography color="text.secondary" sx={{ mt: 2, lineHeight: 1.8 }}>
                        Cuéntame qué necesitas: una aplicación web, una API, automatizar un proceso o
                        poner tu sistema en producción. Te respondo en menos de 24 horas hábiles.
                    </Typography>
                </Box>

                <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2.5}>
                            {status && <Alert severity={status.type}>{status.text}</Alert>}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                                <TextField label="Nombre" value={form.name} onChange={onChange('name')} required
                                    error={!!errors.name} helperText={errors.name} slotProps={{ htmlInput: { maxLength: 100 } }} />
                                <TextField label="Correo electrónico" type="email" value={form.email} onChange={onChange('email')} required
                                    error={!!errors.email} helperText={errors.email} />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                                <TextField label="Teléfono (opcional)" value={form.phone} onChange={onChange('phone')}
                                    slotProps={{ htmlInput: { maxLength: 30 } }} />
                                <TextField label="Asunto" value={form.subject} onChange={onChange('subject')} required
                                    error={!!errors.subject} helperText={errors.subject} slotProps={{ htmlInput: { maxLength: 150 } }} />
                            </Box>
                            <TextField label="Mensaje" value={form.message} onChange={onChange('message')} required
                                multiline minRows={5} error={!!errors.message} helperText={errors.message}
                                slotProps={{ htmlInput: { maxLength: 3000 } }} />

                            {/* Honeypot anti-spam: invisible para personas */}
                            <Box component="input" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                                value={form.website} onChange={onChange('website')}
                                sx={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

                            <Button type="submit" variant="contained" size="large" disabled={sending}
                                endIcon={<SendOutlinedIcon />} sx={{ alignSelf: { sm: 'flex-start' } }}>
                                {sending ? 'Enviando…' : 'Enviar mensaje'}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Box>
        </Section>
    );
}
